import {useCallback, useEffect, useMemo, useState} from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { useCustomSearchParams } from '@selfserve/react-hooks';
import {checklistStatusesTexts} from '../constants/status-texts';
import { AdminSearchInput } from './admin-search-input';
import {
  StyledStatus,
  StyledTable,
  StyledTd,
  StyledTr, StyledType,
} from '../ui/admin-styled-components';
import { TableHeaderButton } from '../ui/table-header-button';
import {checklistTypesTexts} from "../constants/types-texts";
import {AdminPropertyFilter} from "./admin-property-filter";
import {checklistProperties, defaultChecklistProperties, STATUS_VAL} from "../constants/checklist-properties";
import {Scrollbars} from "react-custom-scrollbars";
import {AdminStatusFilter} from "./admin-status-filter";
import {checklistStatusColors} from "../constants/status-colors";
import {AdminChecklistExtendModal} from "./admin-checklist-extand-modal";
import {BeSearchButton} from "../ui/be-search-button";
import {AdminPaginationController} from "./admin-pagination-controller";

const StyledChecklistsListContainer = styled('div')`
  height: calc(100% - 110px);
  margin-top: 24px;
`;

const StyledDropdownContainer = styled('div')`
  margin-left: 16px;
`;

const StyledFiltersContainer = styled('div')`
  width: 100%;
  display: flex;
`;

const StyledScrollbarsContainer = styled('div')`
  margin-top: 24px;
  height: calc(100% - 32px);
`;

const StyledTrackHorizontal = styled('div')`
  height: 10px !important;
  z-index: 2;
  background-color: #fff;
  bottom: 0;
  width: 100%;
  div {
    height: 6px !important;
    margin-top: 4px;
  }
`;

const StyledTrackVertical = styled('div')`
  width: 10px !important;
  z-index: 2;
  background-color: #fff;
  right: 0;
  height: 100%;
  div {
    width: 6px !important;
    margin-left: 4px;
  }
`;

interface Props {
  checklists: any[];
  refetch: () => void;
  handleSetBeFilter: (value: string) => boolean;
}

export const AdminChecklistsList = ({ checklists, refetch, handleSetBeFilter }: Props) => {
  const { t } = useTranslation();
  const [searchByBe, setSearchByBe] = useState(false);
  const [query] = useCustomSearchParams();
  const [search, setSearch] = useState('');
  const [columns, setColumns] = useState(defaultChecklistProperties);
  const [selectedChecklist, setSelectedChecklist] = useState<any>(null);
  const [openModal, setOpenModal] = useState(false);

  useEffect(() => {
    const searchValue = query['search'] as string;
    if (searchValue) {
      const isSearchByBe = handleSetBeFilter(searchValue);
      setSearchByBe(isSearchByBe);
    }
  }, []);

  useEffect(() => {
    if (searchByBe && search !== query['search']) setSearchByBe(false);
  }, [search]);

  const handleSearchButtonClick = useCallback(() => {
    const isSearchByBe = handleSetBeFilter(search);
    setSearchByBe(isSearchByBe);
  }, [search]);

  const handleOpenExtendModal = useCallback(
    (checklist: any) => {
      setSelectedChecklist(checklist);
      setOpenModal(true);
    },
    []
  );

  const handleCloseExtendModal = useCallback(() => {
    setOpenModal(false);
    setSelectedChecklist(null);
  }, []);

  const finalizedList = useMemo(
    () =>
        checklists?.filter(({ name, description, kaltura_application, type, status }: any) => {
          const searchValue = search?.trim()?.toLowerCase();
          if (search && !searchByBe) {
            return (
              name?.toLowerCase()?.includes(searchValue) ||
              description?.toLowerCase()?.includes(searchValue) ||
              kaltura_application?.toLowerCase()?.includes(searchValue) ||
              type?.includes(searchValue) ||
              checklistTypesTexts[type]?.toLowerCase()?.includes(searchValue) ||
              status?.includes(searchValue) ||
              checklistStatusesTexts[status]?.toLowerCase()?.includes(searchValue)
            );
          }
          return true;
        }),
    [checklists, search, searchByBe]
  );

  return (
    <StyledChecklistsListContainer>
      <StyledFiltersContainer>
        <AdminSearchInput value={search} onChange={setSearch} />
        <BeSearchButton onClick={handleSearchButtonClick} />
        <StyledDropdownContainer>
          <AdminPropertyFilter
            properties={checklistProperties}
            onChange={setColumns}
            value={columns}
          />
        </StyledDropdownContainer>
      </StyledFiltersContainer>
      <StyledScrollbarsContainer>
        <Scrollbars
          universal
          height="100%"
          renderTrackHorizontal={(props) => (
            <StyledTrackHorizontal {...props} />
          )}
          renderTrackVertical={(props) => <StyledTrackVertical {...props} />}
        >
          <StyledTable withoutMargin>
            <thead>
            <StyledTr sticky>
              {columns.map(({ text, value, sortingValue }) => (
                value === STATUS_VAL ? (
                  <AdminStatusFilter
                    value={sortingValue || value}
                    statuses={Object.entries(checklistStatusesTexts).map(([value, text]) => ({
                      text,
                      value,
                      className: checklistStatusColors[value],
                    }))}
                  />
                ) : (
                  <TableHeaderButton
                    key={value}
                    value={sortingValue || value}
                  >
                    {text}
                  </TableHeaderButton>
                )
              ))}
            </StyledTr>
            </thead>
            <tbody>
            {finalizedList?.map((checklist: any) => (
              <StyledTr
                key={checklist._id}
                onClick={() => handleOpenExtendModal(checklist)}
              >
                <StyledTd isPairValue={true}>
                  {checklist.name}
                </StyledTd>
                <StyledTd>
                  {checklist.description}
                </StyledTd>
                <StyledTd>
                  {checklist.kaltura_application}
                </StyledTd>
                <StyledTd>
                  <StyledType>
                    {checklistTypesTexts[checklist.type]}
                  </StyledType>
                </StyledTd>
                <StyledTd>
                  <StyledStatus className={checklistStatusColors[checklist.status]}>
                    {checklistStatusesTexts[checklist.status]}
                  </StyledStatus>
                </StyledTd>
              </StyledTr>
            ))}
            </tbody>
          </StyledTable>
        </Scrollbars>
      </StyledScrollbarsContainer>
      {selectedChecklist && (
        <AdminChecklistExtendModal
          open={openModal}
          checklistJson={selectedChecklist}
          handleClose={handleCloseExtendModal}
          refetch={refetch}
          checklistId={selectedChecklist._id}
        />
      )}
      <AdminPaginationController />
    </StyledChecklistsListContainer>
  );
};
